import type { ProgressState } from "./progress-state";
import {
  gameplayConfig,
  type SkinConfig,
  type SkinId,
} from "../config/gameplay-config";

export type SkinUnlockStatus =
  | "already-unlocked"
  | "insufficient-funds"
  | "invalid-skin"
  | "invalid-state"
  | "stale-command"
  | "unlocked";

export type SkinSelectionStatus =
  | "already-selected"
  | "invalid-skin"
  | "invalid-state"
  | "locked"
  | "selected"
  | "stale-command";

export interface SkinTransition<Status> {
  readonly state: ProgressState;
  readonly status: Status;
}

const skinById = new Map<SkinId, SkinConfig>(
  gameplayConfig.skins.map((skin) => [skin.id, skin]),
);

export const getSkinConfig = (id: SkinId): SkinConfig => {
  const skin = skinById.get(id);
  if (skin === undefined) {
    throw new Error(`Skin is not configured: ${id}`);
  }
  return skin;
};

const resolveSkin = (id: string): SkinConfig | undefined =>
  gameplayConfig.skins.find((skin) => skin.id === id);

const hasValidSkinState = (state: ProgressState): boolean => {
  if (
    !Number.isSafeInteger(state.totalCoins) ||
    state.totalCoins < 0 ||
    state.unlockedSkinIds.length === 0 ||
    !state.unlockedSkinIds.includes(gameplayConfig.defaultSkinId) ||
    !state.unlockedSkinIds.includes(state.selectedSkinId)
  ) {
    return false;
  }
  const unique = new Set(state.unlockedSkinIds);
  return (
    unique.size === state.unlockedSkinIds.length &&
    state.unlockedSkinIds.every((id) => skinById.has(id))
  );
};

export const unlockSkin = (
  state: ProgressState,
  id: string,
  expectedCoins: number,
): SkinTransition<SkinUnlockStatus> => {
  const skin = resolveSkin(id);
  if (skin === undefined) {
    return { state, status: "invalid-skin" };
  }
  const cost = skin.unlockCost;
  if (cost === null) {
    return { state, status: "already-unlocked" };
  }
  if (!hasValidSkinState(state)) {
    return { state, status: "invalid-state" };
  }
  if (state.unlockedSkinIds.includes(skin.id)) {
    return { state, status: "already-unlocked" };
  }
  if (
    !Number.isSafeInteger(expectedCoins) ||
    expectedCoins < 0 ||
    expectedCoins !== state.totalCoins
  ) {
    return { state, status: "stale-command" };
  }
  if (state.totalCoins < cost) {
    return { state, status: "insufficient-funds" };
  }
  return {
    state: {
      ...state,
      totalCoins: state.totalCoins - cost,
      unlockedSkinIds: [...state.unlockedSkinIds, skin.id],
    },
    status: "unlocked",
  };
};

export const selectSkin = (
  state: ProgressState,
  id: string,
  expectedSelectedSkinId: string,
): SkinTransition<SkinSelectionStatus> => {
  const skin = resolveSkin(id);
  if (skin === undefined) {
    return { state, status: "invalid-skin" };
  }
  if (!hasValidSkinState(state)) {
    return { state, status: "invalid-state" };
  }
  if (state.selectedSkinId === skin.id) {
    return { state, status: "already-selected" };
  }
  if (expectedSelectedSkinId !== state.selectedSkinId) {
    return { state, status: "stale-command" };
  }
  if (!state.unlockedSkinIds.includes(skin.id)) {
    return { state, status: "locked" };
  }
  return {
    state: { ...state, selectedSkinId: skin.id },
    status: "selected",
  };
};
